import { io, Socket } from 'socket.io-client';
import store from '../Redux/Store';
import { getWebSocketConfig, isWebSocketEnabled } from '../Config/AppConfig';
import { GenericActions } from '../Redux/Generic/GenericAction';

/**
 * WebSocketService - Gestisce la connessione Socket.IO per eventi real-time
 * 
 * Eventi ricevuti dal server:
 * - 'connected': Conferma connessione stabilita
 * - 'new-alarm': Nuovo allarme creato
 * - 'sensor-update': Aggiornamento valori sensori
 * 
 * Funzionalità:
 * - Autenticazione JWT automatica
 * - Riconnessione automatica
 * - Fallback a HTTP polling se WebSocket fallisce
 * - Configurazione centralizzata tramite AppConfig
 */
class WebSocketService {
  private socket: Socket | null = null;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;

  /**
   * Connette al server Socket.IO con autenticazione JWT
   * @param token - Token JWT per autenticazione
   */
  connect(token: string): void {
    // Verifica se WebSocket è abilitato nella configurazione
    if (!isWebSocketEnabled()) {
      console.log('⚠️ WebSocket disabilitato nella configurazione. Usa HTTP polling.');
      return;
    }

    if (this.socket?.connected) {
      console.log('⚠️ WebSocket già connesso');
      return;
    }

    // Ottieni configurazione WebSocket da AppConfig
    const wsConfig = getWebSocketConfig();

    console.log(`🔌 Connessione a Socket.IO server: ${wsConfig.url}`);

    this.socket = io(wsConfig.url, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'], // Prova WebSocket, fallback a polling
      reconnection: true,
      reconnectionAttempts: wsConfig.reconnectionAttempts,
      reconnectionDelay: wsConfig.reconnectionDelay,
      reconnectionDelayMax: wsConfig.reconnectionDelay * 5,
      timeout: wsConfig.timeout
    });

    this.setupEventListeners();
  }

  /**
   * Configura i listener per gli eventi Socket.IO
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Evento: Connessione stabilita
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      console.log('✅ Socket.IO connesso (socketId:', this.socket?.id, ')');
    });

    // Evento: Messaggio di benvenuto dal server
    this.socket.on('connected', (data: any) => {
      console.log('📡 Socket.IO:', data.message);
    });

    // Evento: Nuovo allarme ricevuto
    this.socket.on('new-alarm', (alarmData: any) => {
      console.log('🚨 Nuovo allarme ricevuto via WebSocket:', alarmData);
      this.handleNewAlarm(alarmData);
    });

    // Evento: Aggiornamento sensori ricevuto
    this.socket.on('sensor-update', (sensorData: any) => {
      console.log('📡 Aggiornamento sensori ricevuto via WebSocket:', sensorData);
      this.handleSensorUpdate(sensorData);
    });

    // Evento: Disconnessione
    this.socket.on('disconnect', (reason: string) => {
      this.isConnected = false;
      console.log('🔌 Socket.IO disconnesso. Motivo:', reason);
      
      if (reason === 'io server disconnect') {
        // Server ha forzato disconnessione, riconnetti manualmente
        console.log('⚠️ Server ha chiuso la connessione. Tentativo riconnessione...');
        this.socket?.connect();
      }
    });

    // Evento: Errore di connessione
    this.socket.on('connect_error', (error: Error) => {
      this.reconnectAttempts++;
      const wsConfig = getWebSocketConfig();
      console.error(`❌ Errore connessione Socket.IO (tentativo ${this.reconnectAttempts}/${wsConfig.reconnectionAttempts}):`, error.message);
      
      if (this.reconnectAttempts >= wsConfig.reconnectionAttempts) {
        console.error('❌ Raggiunto limite tentativi riconnessione. Fallback a HTTP polling.');
        this.disconnect();
      }
    });

    // Evento: Tentativo di riconnessione
    this.socket.on('reconnect_attempt', (attemptNumber: number) => {
      console.log(`🔄 Tentativo riconnessione Socket.IO #${attemptNumber}...`);
    });

    // Evento: Riconnessione riuscita
    this.socket.on('reconnect', (attemptNumber: number) => {
      console.log(`✅ Socket.IO riconnesso dopo ${attemptNumber} tentativi`);
      this.isConnected = true;
      this.reconnectAttempts = 0;
    });

    // Evento: Errore generico
    this.socket.on('error', (error: any) => {
      console.error('❌ Errore Socket.IO:', error);
    });
  }

  /**
   * Gestisce l'arrivo di un nuovo allarme via WebSocket
   * @param alarmData - Dati dell'allarme ricevuto
   */
  private handleNewAlarm(alarmData: any): void {
    try {
      // Dispatch Redux action per aggiungere allarme allo store
      store.dispatch(GenericActions.addNewAlarmFromWebSocket(alarmData));
      
      console.log('🚨 Nuovo allarme dispatched a Redux:', {
        id: alarmData.id,
        sensorType: alarmData.sensorType,
        patientName: alarmData.patientName,
        value: alarmData.value,
        timestamp: alarmData.timestamp
      });

      // ⚠️ IMPORTANTE: Triggera reload degli allarmi per aggiornare AlarmGalleryHandler
      // Questo forza il componente a ricaricare la lista allarmi via HTTP
      document.dispatchEvent(new Event('reload-all-alarms'));

      // Niente notifica del browser qui: la notifica dell'allarme arriva dal server (Web Push), con nome e posizione.
      // Questa vecchia, con il permesso dato per le push, compariva in piu' come «🚨 Nuovo Allarme ... paziente
      // Sconosciuto» (02/11/2026).
    } catch (error) {
      console.error('❌ Errore gestione nuovo allarme:', error);
    }
  }

  /**
   * Gestisce l'aggiornamento dei sensori via WebSocket
   * @param sensorData - Dati dei sensori aggiornati
   */
  private handleSensorUpdate(sensorData: any): void {
    try {
      // Dispatch Redux action per aggiornare sensori nello store
      store.dispatch(GenericActions.updateSentinelSensorsFromWebSocket(sensorData));
      
      console.log('📡 Aggiornamento sensori dispatched a Redux:', {
        sentinelId: sensorData.sentinelId,
        patientId: sensorData.patientId,
        sensorsCount: sensorData.sensors?.length || 0
      });
    } catch (error) {
      console.error('❌ Errore gestione aggiornamento sensori:', error);
    }
  }

  /**
   * Disconnette dal server Socket.IO
   */
  disconnect(): void {
    if (this.socket) {
      console.log('🔌 Disconnessione Socket.IO...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.reconnectAttempts = 0;
    }
  }

  /**
   * Verifica se il WebSocket è connesso
   * @returns true se connesso, false altrimenti
   */
  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  /**
   * Ottiene l'ID del socket corrente
   * @returns Socket ID o null se non connesso
   */
  getSocketId(): string | null {
    return this.socket?.id || null;
  }

  /**
   * Richiede permesso per notifiche browser
   */
  async requestNotificationPermission(): Promise<void> {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      console.log('🔔 Permesso notifiche:', permission);
    }
  }
}

// Esporta istanza singleton
export default new WebSocketService();
